import type { InstanceDetail, ItemStatus, NodeAttributeValue, SegmentStatus, SetNodeAttributeRequest } from "@keydials/api-client";

// ============================================================
// Optimistic merges into the loaded InstanceDetail (KEY-248 latency work).
//
// The write endpoints return 204, so historically the client learned the outcome of its own writes only
// by refetching the whole workspace — and the parked-pick auto-commit (which waits to SEE the saved
// attribute value) was serialised behind that six-request reload. These helpers fold the client's own
// accepted write into the detail it already holds, so dependent UI reacts after one round trip; the
// debounced background reload then reconciles everything derived server-side (rollups, bands, audit
// fields). Each helper returns a NEW detail object — memoised consumers (buildDialVM) must recompute.
//
// Placeholder audit fields (updatedAtUtc/updatedByEmail) are deliberately blank-ish on inserted rows:
// nothing user-facing renders them between the merge and the reconciling reload.
// ============================================================

/** Fold an accepted node-attribute write into the detail (upsert by node + attribute). */
export function mergeNodeAttributeValue(detail: InstanceDetail, request: SetNodeAttributeRequest): InstanceDetail {
  const values = [...(detail.nodeAttributeValues ?? [])];
  const idx = values.findIndex((v) =>
    v.nodeType === request.target
    && v.attributeKey === request.attributeKey
    && (v.sectionKey ?? "") === (request.sectionKey ?? "")
    && (v.segmentKey ?? "") === (request.segmentKey ?? "")
    && (v.itemKey ?? "") === (request.itemKey ?? ""));
  const row: NodeAttributeValue = {
    nodeType: request.target,
    sectionKey: request.sectionKey ?? "",
    segmentKey: request.segmentKey ?? "",
    itemKey: request.itemKey ?? "",
    attributeKey: request.attributeKey,
    value: request.value,
    updatedAtUtc: idx >= 0 ? values[idx].updatedAtUtc : "",
    updatedByEmail: idx >= 0 ? values[idx].updatedByEmail : "",
  };
  if (idx >= 0) {
    values[idx] = row;
  } else {
    values.push(row);
  }
  return { ...detail, nodeAttributeValues: values };
}

/** Fold an item status change into the detail (upsert by item + dimension); `prior` for a revert. */
export function mergeItemStatus(
  detail: InstanceDetail,
  entry: { sectionKey: string; segmentKey: string; itemKey: string; dimensionKey: string; statusKey: string },
): InstanceDetail {
  const statuses = [...(detail.itemStatuses ?? [])];
  const idx = statuses.findIndex((s) =>
    s.sectionKey === entry.sectionKey && s.segmentKey === entry.segmentKey
    && s.itemKey === entry.itemKey && s.dimensionKey === entry.dimensionKey);
  const row: ItemStatus = idx >= 0
    ? { ...statuses[idx], statusKey: entry.statusKey }
    : { ...entry, updatedAtUtc: "", updatedByEmail: "" };
  if (idx >= 0) {
    statuses[idx] = row;
  } else {
    statuses.push(row);
  }
  return { ...detail, itemStatuses: statuses };
}

/** The current item-status row for a node, or null when unset — capture it before an optimistic flip. */
export function findItemStatus(
  detail: InstanceDetail,
  key: { sectionKey: string; segmentKey: string; itemKey: string; dimensionKey: string },
): ItemStatus | null {
  return (detail.itemStatuses ?? []).find((s) =>
    s.sectionKey === key.sectionKey && s.segmentKey === key.segmentKey
    && s.itemKey === key.itemKey && s.dimensionKey === key.dimensionKey) ?? null;
}

/** Drop an item's status row entirely (reverting an optimistic flip on a previously-unset node). */
export function removeItemStatus(
  detail: InstanceDetail,
  key: { sectionKey: string; segmentKey: string; itemKey: string; dimensionKey: string },
): InstanceDetail {
  return {
    ...detail,
    itemStatuses: (detail.itemStatuses ?? []).filter((s) =>
      !(s.sectionKey === key.sectionKey && s.segmentKey === key.segmentKey
        && s.itemKey === key.itemKey && s.dimensionKey === key.dimensionKey)),
  };
}

/** Fold a section status change (KEY-479) into the detail: upsert by section, or drop the row on null. */
export function mergeSectionStatus(
  detail: InstanceDetail,
  entry: { sectionKey: string; statusKey: string | null },
): InstanceDetail {
  const rows = (detail.sectionStatuses ?? []).filter((s) => s.sectionKey !== entry.sectionKey);
  if (entry.statusKey !== null) {
    rows.push({ sectionKey: entry.sectionKey, statusKey: entry.statusKey, updatedAtUtc: "", updatedByEmail: "" });
  }
  return { ...detail, sectionStatuses: rows };
}

/** Fold a segment status change into the detail (upsert by segment); other fields ride along. */
export function mergeSegmentStatus(
  detail: InstanceDetail,
  entry: { sectionKey: string; segmentKey: string; statusKey: string },
): InstanceDetail {
  const statuses = [...(detail.statuses ?? [])];
  const idx = statuses.findIndex((s) => s.sectionKey === entry.sectionKey && s.segmentKey === entry.segmentKey);
  const row: SegmentStatus = idx >= 0
    ? { ...statuses[idx], statusKey: entry.statusKey }
    : { ...entry, reviewDate: null, updatedAtUtc: "", updatedByEmail: "" };
  if (idx >= 0) {
    statuses[idx] = row;
  } else {
    statuses.push(row);
  }
  return { ...detail, statuses };
}

/** The current segment-status row, or null when unset — capture it before an optimistic flip. */
export function findSegmentStatus(
  detail: InstanceDetail,
  key: { sectionKey: string; segmentKey: string },
): SegmentStatus | null {
  return (detail.statuses ?? []).find((s) => s.sectionKey === key.sectionKey && s.segmentKey === key.segmentKey) ?? null;
}

/** Drop a segment's status row (reverting an optimistic flip on a previously-unset segment). */
export function removeSegmentStatus(
  detail: InstanceDetail,
  key: { sectionKey: string; segmentKey: string },
): InstanceDetail {
  return {
    ...detail,
    statuses: (detail.statuses ?? []).filter((s) => !(s.sectionKey === key.sectionKey && s.segmentKey === key.segmentKey)),
  };
}
